import type { SubmitScanRequest } from '@/api/types'
import type { ShenAiMeasurementResults } from './shenai-types'

/**
 * Turns Shen's measurement into the one shape this platform submits, or null when there is nothing
 * worth submitting.
 *
 * The same two rules the other two vendors' mappers follow, for the same reasons.
 *
 * **A non-positive value is a missing value.** Every vendor here reports 0 or a negative for
 * something it could not measure, and `SubmitScanRequest` requires sbp, dbp and hr - so a result
 * missing any of those three is a failed scan the patient is offered another go at, not a
 * submission with a hole in it.
 *
 * **Blood oxygen is OMITTED, not zeroed.** Shen measures no SpO2 at all: there is no field for it
 * on the vendor's own `MeasurementResults`. `spo2` is optional on the submit body, so the honest
 * encoding of "this vendor does not measure it" is to leave the property off - which is what the
 * FaceHeart and Mindset mappers already do for a vital that came back unusable. A `-1` or a `0`
 * would be a NUMBER in a field every consumer downstream of `VitalsAssessmentCompletedEvent` reads
 * as a measurement, and nothing about it would say which vendor produced it or that it means
 * "never measured" (docs/decisions.md D-32).
 */
export function toSubmitRequestFromShenAi(
  results: ShenAiMeasurementResults | null | undefined,
): SubmitScanRequest | null {
  if (!results) {
    return null
  }

  const hr = results.heart_rate_bpm
  const rr = results.breathing_rate_bpm
  const bp = shenAiBloodPressure(results)

  if (!isPositive(hr) || !bp) {
    return null
  }

  return {
    sbp: bp.sbp,
    dbp: bp.dbp,
    hr: Math.round(hr),
    ...(isPositive(rr) ? { rr: Math.round(rr) } : {}),
  }
}

/**
 * This vendor's blood pressure, rounded to the integers the platform stores, or null when it
 * measured neither number usably.
 *
 * **Exported because it is the ONE vital a hybrid scan takes from this vendor** (docs/decisions.md
 * D-37): the composite is this blood pressure beside Mindset's pulse and respiration, so the hybrid
 * mapper needs the pressure on its own rather than a whole `SubmitScanRequest` that also fails when
 * this vendor's heart rate is missing - a heart rate a hybrid scan does not use.
 *
 * BOTH NUMBERS OR NEITHER. A systolic with no diastolic is not half a blood pressure; it is a
 * reading with a hole in it, and `SubmitScanRequest` requires the pair.
 */
export function shenAiBloodPressure(
  results: ShenAiMeasurementResults | null | undefined,
): { sbp: number; dbp: number } | null {
  const sbp = results?.systolic_blood_pressure_mmhg
  const dbp = results?.diastolic_blood_pressure_mmhg

  return isPositive(sbp) && isPositive(dbp)
    ? { sbp: Math.round(sbp), dbp: Math.round(dbp) }
    : null
}

function isPositive(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}
